import {
  CheckCircle2,
  UserPlus,
  Link,
  X,
  Users,
  AlertCircle,
  Info
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Upload, ExtractedIdentifier, UnmatchedIdentifierAction, DriverMaster, DriverMatchResult } from '@/types'

interface SkippedStepInfo {
  reason: string
  details: string
}

interface DriverActionsConfirmationStepProps {
  driverMatches: DriverMatchResult[]
  availableDrivers: DriverMaster[]
  unmatchedActions: Map<number, UnmatchedIdentifierAction>
  perfectMatchOverrides: Map<number, UnmatchedIdentifierAction>
  ignoredRows: Set<number>
  autoAcceptMatches: boolean
  platform: string
  skippedDriverReview?: SkippedStepInfo
  skippedFieldMapping?: SkippedStepInfo
}

export function DriverActionsConfirmationStep({
  driverMatches,
  availableDrivers,
  unmatchedActions,
  perfectMatchOverrides,
  ignoredRows,
  autoAcceptMatches,
  platform,
  skippedDriverReview,
  skippedFieldMapping
}: DriverActionsConfirmationStepProps) {
  if (!driverMatches || driverMatches.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Inga förarmatchningar tillgängliga</p>
      </div>
    )
  }

  // Calculate actions
  const perfectMatches = driverMatches.filter(match => match.matchedDriverId !== null)
  const autoAcceptedMatches = autoAcceptMatches
    ? perfectMatches.filter(match =>
        !perfectMatchOverrides.has(match.rowIndex) &&
        !ignoredRows.has(match.rowIndex) &&
        !unmatchedActions.has(match.rowIndex)
      )
    : []

  const matchesWithOverrides = Array.from(perfectMatchOverrides.entries()).filter(([rowIndex]) =>
    !ignoredRows.has(rowIndex)
  )

  const unmatchedWithActions = Array.from(unmatchedActions.entries()).filter(([rowIndex]) =>
    !ignoredRows.has(rowIndex)
  )

  const newDriversToCreate = [
    ...matchesWithOverrides.filter(([, action]) => action.action === 'create_new'),
    ...unmatchedWithActions.filter(([, action]) => action.action === 'create_new')
  ]

  // Separate auto-accepted matches into those that need aliases vs direct matches
  // Use the needsAlias field from the match result (set during driver matching)
  const autoAcceptedDirectMatches = autoAcceptedMatches.filter(match =>
    !match.needsAlias
  )

  const autoAcceptedAliasMatches = autoAcceptedMatches.filter(match =>
    match.needsAlias === true
  )

  const aliasesToCreate = [
    ...matchesWithOverrides.filter(([, action]) => action.action === 'select_existing'),
    ...unmatchedWithActions.filter(([, action]) => action.action === 'select_existing')
  ]

  // Build action list
  const actions: Array<{ name: string; action: string }> = []

  // Auto-accepted matches
  autoAcceptedMatches.forEach(match => {
    const driver = availableDrivers.find(d => d.id === match.matchedDriverId)
    actions.push({
      name: match.extractedName || match.identifiers.name || 'Okänt',
      action: `Uppdatera ${driver?.primaryName || 'förare'}`
    })
  })

  // New drivers
  newDriversToCreate.forEach(([rowIndex, actionData]) => {
    const match = driverMatches.find(m => m.rowIndex === rowIndex)
    actions.push({
      name: match?.extractedName || match?.identifiers.name || actionData.newDriverName || 'Okänt',
      action: 'Skapa ny förare'
    })
  })

  // Aliases
  aliasesToCreate.forEach(([rowIndex, actionData]) => {
    const match = driverMatches.find(m => m.rowIndex === rowIndex)
    const targetDriver = availableDrivers.find(d => d.id === actionData.selectedDriverId)
    actions.push({
      name: match?.extractedName || match?.identifiers.name || 'Okänt',
      action: `Länka till ${targetDriver?.primaryName || 'förare'}`
    })
  })

  // Helper function to get action icon
  const getActionIcon = (action: string) => {
    switch (action) {
      case 'create_new': return UserPlus
      case 'select_existing': return Link
      case 'ignore': return X
      default: return Users
    }
  }

  // Helper function to get driver name
  const getDriverName = (driverId: string) => {
    const driver = availableDrivers.find(d => d.id === driverId)
    return driver?.primaryName || 'Okänd förare'
  }

  const totalIgnored = ignoredRows.size
  const totalAliasesToCreate = aliasesToCreate.length + autoAcceptedAliasMatches.length

  return (
    <div className="space-y-6">
      {/* Skipped steps notifications */}
      {(skippedFieldMapping || skippedDriverReview) && (
        <div className="space-y-3">
          {skippedFieldMapping && (
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertTitle className="text-green-800">{skippedFieldMapping.reason}</AlertTitle>
              <AlertDescription className="text-green-700">
                {skippedFieldMapping.details}
              </AlertDescription>
            </Alert>
          )}
          {skippedDriverReview && (
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertTitle className="text-green-800">{skippedDriverReview.reason}</AlertTitle>
              <AlertDescription className="text-green-700">
                {skippedDriverReview.details}
              </AlertDescription>
            </Alert>
          )}
        </div>
      )}

      {/* Auto-accepted matches (direct matches only, no aliases needed) */}
      {autoAcceptedDirectMatches.length > 0 && (
        <div>
          <h3 className="text-green-800 font-semibold flex items-center gap-2 mb-3">
            <CheckCircle2 className="h-5 w-5" />
            Befintliga förare ({autoAcceptedDirectMatches.length})
          </h3>
          <div className="text-sm text-green-700 mb-3">
            Dessa förare finns redan i systemet. Nya inkomster kommer läggas till deras profiler.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {autoAcceptedDirectMatches.map(match => (
              <div key={match.rowIndex} className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-200">
                <span className="text-sm font-medium">{match.extractedName || 'Okänt namn'}</span>
                <Badge variant="outline" className="text-green-700 bg-green-100">
                  → {match.matchedDriverName}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* New drivers to create */}
      {newDriversToCreate.length > 0 && (
        <div>
          <h3 className="text-blue-800 font-semibold flex items-center gap-2 mb-3">
            <UserPlus className="h-5 w-5" />
            Nya förare kommer skapas ({newDriversToCreate.length})
          </h3>
          <div className="text-sm text-blue-700 mb-3">
            Nya förarprofiler kommer skapas för dessa personer.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {newDriversToCreate.map(([rowIndex, action]) => {
              const driverMatch = driverMatches.find(match => match.rowIndex === rowIndex)
              const Icon = getActionIcon(action.action)
              return (
                <div key={rowIndex} className="flex items-center justify-between p-2 bg-blue-50 rounded border border-blue-200">
                  <span className="text-sm font-medium">
                    {driverMatch?.extractedName || 'Okänt namn'}
                  </span>
                  <Badge variant="outline" className="text-blue-700 bg-blue-100">
                    <Icon className="h-3 w-3 mr-1" />
                    {action.newDriverName}
                  </Badge>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Aliases to create */}
      {totalAliasesToCreate > 0 && (
        <div>
          <h3 className="text-purple-800 font-semibold flex items-center gap-2 mb-3">
            <Link className="h-5 w-5" />
            Alias kommer skapas ({totalAliasesToCreate})
          </h3>
          <div className="text-sm text-purple-700 mb-3">
            Dessa alias kommer läggas till för att länka plattformsdata till förare.
          </div>
          <div className="space-y-2">
            {/* Auto-accepted aliases (matches that need new aliases) */}
            {autoAcceptedAliasMatches.map(match => (
              <div key={match.rowIndex} className="flex items-center justify-between p-2 bg-purple-50 rounded border border-purple-200">
                <span className="text-sm">
                  <span className="font-medium">{match.extractedName || 'Okänt namn'}</span>
                  <span className="text-muted-foreground"> på {platform}</span>
                </span>
                <Badge variant="outline" className="text-purple-700 bg-purple-100">
                  → {match.matchedDriverName}
                </Badge>
              </div>
            ))}

            {/* Manual aliases */}
            {[...matchesWithOverrides, ...unmatchedWithActions]
              .filter(([, action]) => action.action === 'select_existing')
              .map(([rowIndex, action]) => {
                const driverMatch = driverMatches.find(match => match.rowIndex === rowIndex)
                return (
                  <div key={rowIndex} className="flex items-center justify-between p-2 bg-purple-50 rounded border border-purple-200">
                    <span className="text-sm">
                      <span className="font-medium">{driverMatch?.extractedName || 'Okänt namn'}</span>
                      <span className="text-muted-foreground"> på {platform}</span>
                    </span>
                    <Badge variant="outline" className="text-purple-700 bg-purple-100">
                      → {action.selectedDriverId ? getDriverName(action.selectedDriverId) : 'Okänd'}
                    </Badge>
                  </div>
                )
              })}
          </div>
        </div>
      )}

      {/* Ignored items */}
      {totalIgnored > 0 && (
        <div>
          <h3 className="text-gray-800 font-semibold flex items-center gap-2 mb-3">
            <X className="h-5 w-5" />
            Ignorerade rader ({totalIgnored})
          </h3>
          <div className="text-sm text-gray-700 mb-3">
            Dessa rader hoppas över och kommer inte bearbetas.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {Array.from(ignoredRows).map(rowIndex => {
              const driverMatch = driverMatches.find(match => match.rowIndex === rowIndex)
              return (
                <div key={rowIndex} className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200">
                  <span className="text-sm font-medium text-gray-600">
                    {driverMatch?.extractedName || `Rad ${rowIndex + 1}`}
                  </span>
                  <Badge variant="outline" className="text-gray-600 bg-gray-100">
                    Ignorerad
                  </Badge>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Warning if no actions */}
      {autoAcceptedDirectMatches.length === 0 && newDriversToCreate.length === 0 && totalAliasesToCreate === 0 && totalIgnored === 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Inga åtgärder valda. Gå tillbaka och välj vad som ska göras med de omatchade förarna.
          </AlertDescription>
        </Alert>
      )}
    </div>
  )
}
